import React from 'react';
import PropTypes from 'prop-types';
import {
  FaFilter,
  FaRobot,
  FaBuilding,
  FaSitemap,
  FaCalendarAlt,
  FaSearch,
  FaLightbulb,
  FaChevronDown
} from 'react-icons/fa';
import {
  formatDisplayDate,
  getDaysAgoIsoDate,
  getTodayIsoDate
} from '../../../analytics/endUser/shared/heatmapViewUtils';

export default function CustomerAnalyticsSkeleton() {
  const fromDisplay = formatDisplayDate(getDaysAgoIsoDate(7));
  const toDisplay = formatDisplayDate(getTodayIsoDate());
  return (
    <div
      className="skeleton-wireframe w-full flex flex-col gap-6 text-slate-800"
      aria-hidden="true"
      data-testid="customer-analytics-skeleton"
    >
      {/* 1. Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Behavioural Analytics</h1>
        <p className="text-xs text-slate-500">
          Analyze user behavioral funnels, multi-dimensional audience breakdowns, and journey paths.
        </p>
      </div>

      {/* 2. Navigation Tabs */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          tabIndex={-1}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold shadow-sm"
        >
          <FaRobot className="text-white" /> Journey Mining
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 shadow-sm"
        >
          <FaFilter className="text-slate-500" /> Funnels & Segments
        </button>
      </div>

      {/* 3. Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-4 shadow-sm text-xs">
        {/* Organization */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaBuilding className="text-slate-400" /> Organization
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[150px] text-slate-800 font-medium">
            <span>System</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        {/* Site Collection */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaSitemap className="text-slate-400" /> Site Collection
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[170px] text-slate-400 font-medium">
            <span>Loading sites...</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        {/* From Date */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">From</span>
          <div className="flex items-center justify-between gap-2 bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 min-w-[120px]">
            <span>{fromDisplay}</span>
            <FaCalendarAlt className="text-slate-400 text-[11px]" />
          </div>
        </div>

        {/* To Date */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">To</span>
          <div className="flex items-center justify-between gap-2 bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 min-w-[120px]">
            <span>{toDisplay}</span>
            <FaCalendarAlt className="text-slate-400 text-[11px]" />
          </div>
        </div>
      </div>

      {/* 4. Journey Mining Banner (Dark Blue Gradient) */}
      <div className="bg-gradient-to-r from-[#111827] via-[#1e1b4b] to-[#2e1065] text-white rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6 shadow-md border border-indigo-950">
        <div className="max-w-2xl flex flex-col gap-2">
          <h2 className="text-xl font-bold text-white tracking-tight">
            Behavioral Journey Mining & Golden Flow Review
          </h2>
          <p className="text-xs text-indigo-200/90 leading-relaxed">
            Review detected user journeys grouped by business process, evaluate validated sequence support, and
            establish the definitive Golden Flow to push directly into Content Studio.
          </p>
        </div>

        <div className="flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-4 flex-col min-w-[140px]">
          <span className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest mb-1">
            Analyzed Journeys
          </span>
          <span className="text-3xl font-extrabold text-white">0</span>
        </div>
      </div>

      {/* 5. Flow Search Input */}
      <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3.5 py-2 max-w-sm text-xs text-slate-400 shadow-xs">
        <FaSearch className="text-slate-400" />
        <span>Search flows (e.g. &apos;all-services&apos;, &apos;checkout&apos;, &apos;submit&apos;)...</span>
      </div>

      {/* 6. Empty State Card */}
      <div className="border border-dashed border-slate-300 rounded-2xl p-14 flex flex-col items-center justify-center text-center bg-white/60 shadow-xs">
        <FaLightbulb className="text-3xl text-slate-400 mb-3" />
        <h3 className="text-base font-bold text-slate-800 mb-1">No Candidate Patterns Discovered</h3>
        <p className="text-xs text-slate-500 max-w-md leading-relaxed">
          Ensure capture is active and sufficient user interactions exist in the selected measurement window.
        </p>
      </div>
    </div>
  );
}

CustomerAnalyticsSkeleton.propTypes = {
  featureName: PropTypes.string
};
